import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  TemplateRef,
  EventEmitter,
  Output,
  OnInit
} from '@angular/core';

import { Subject } from 'rxjs';

import {
  CalendarEvent,
  CalendarMonthViewDay,
  CalendarView,
  CalendarDateFormatter,
  DAYS_OF_WEEK
} from 'angular-calendar';

import { CustomDateFormatter } from './custom-date-formatter.provider';

@Component({
  selector: 'app-calendar-month',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './calendar-month.component.html',
  styleUrls: ['./calendar-month.component.css'],
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: CustomDateFormatter,
    },
  ],
})
export class CalendarMonthComponent implements OnInit {

  @ViewChild('modalContent', { static: true })
  modalContent: TemplateRef<any>;

  @Output()
  messageEvent = new EventEmitter<CalendarMonthViewDay>();

  @Output()
  messageMonth = new EventEmitter<any>();

  view: CalendarView = CalendarView.Month;

  CalendarView = CalendarView;

  /**
   * Mes actualmente visible.
   */
  viewDate: Date = new Date();

  locale: string = 'es';

  weekStartsOn: number = DAYS_OF_WEEK.MONDAY;

  weekendDays: number[] = [
    DAYS_OF_WEEK.SUNDAY
  ];

  clickedDate: Date;

  refresh: Subject<any> = new Subject<any>();

  events: CalendarEvent[] = [];

  activeDayIsOpen: boolean = false;

  selectedDay: CalendarMonthViewDay = null;

  /**
   * Día inicial YYYYMMDD
   */
  dia_inicial: string = '';

  /**
   * Mes actual del calendario.
   */
  month_calendar: any = {
    year: 0,
    month: 0
  };

  /**
   * Día seleccionado YYYYMMDD
   */
  selectedDateInt: string = '';

  /**
   * Controla que el día inicial solo se emita una vez.
   */
  emitioDiaInicial: boolean = false;

  constructor() { }

  ngOnInit(): void {

    const hoy = new Date();

    this.viewDate = hoy;

    this.month_calendar = {
      year: hoy.getFullYear(),
      month: hoy.getMonth() + 1
    };
  }

  /**
   * Convierte fecha a YYYYMMDD.
   */
  private getDateInt(dia: Date): string {

    return `${dia.getFullYear()}${(
      dia.getMonth() + 101
    ).toString().substring(1, 3)}${(
      dia.getDate() + 100
    ).toString().substring(1, 3)}`;
  }

  /**
   * Marca un día visualmente.
   */
  private marcarDiaSeleccionado(
    day: CalendarMonthViewDay
  ): void {

    if (this.selectedDay) {
      this.selectedDay.cssClass = '';
    }

    day.cssClass = 'amc-cal-day-selected';

    this.selectedDay = day;
  }

  /**
   * Click sobre un día.
   */
  dayEnabledClicked(
    day: CalendarMonthViewDay
  ): void {

    this.dia_inicial = '';

    this.selectedDateInt = this.getDateInt(
      day.date
    );

    this.marcarDiaSeleccionado(day);

    this.messageEvent.emit(day);

    this.refresh.next();
  }

  /**
   * Recibe las horas desde el componente padre.
   *
   * IMPORTANTE:
   * Si dia_inicial es null, solamente actualiza
   * los eventos y NO modifica el mes visible.
   */
  sendToCalendar(
    hours: CalendarEvent[],
    dia_inicial: Date = null
  ): void {

    this.events = hours || [];

    /**
     * Solo cambiamos el mes cuando realmente
     * viene una fecha inicial.
     */
    if (dia_inicial) {

      const fechaInicial = new Date(
        dia_inicial.getFullYear(),
        dia_inicial.getMonth(),
        dia_inicial.getDate()
      );

      this.viewDate = fechaInicial;

      this.dia_inicial = this.getDateInt(
        fechaInicial
      );

      this.selectedDateInt =
        this.dia_inicial;

      this.emitioDiaInicial = false;

      this.month_calendar = {
        year: fechaInicial.getFullYear(),
        month: fechaInicial.getMonth() + 1
      };
    }

    this.refresh.next();
  }

  /**
   * Mes siguiente.
   */
  nextMes(): void {

    const nuevaFecha = new Date(
      this.viewDate.getFullYear(),
      this.viewDate.getMonth() + 1,
      1
    );

    this.viewDate = nuevaFecha;

    this.month_calendar = {
      year: nuevaFecha.getFullYear(),
      month: nuevaFecha.getMonth() + 1
    };

    this.limpiarSeleccionDia();

    this.messageMonth.emit(
      this.month_calendar
    );

    this.refresh.next();
  }

  /**
   * Mes anterior.
   */
  prevMes(): void {

    const nuevaFecha = new Date(
      this.viewDate.getFullYear(),
      this.viewDate.getMonth() - 1,
      1
    );

    this.viewDate = nuevaFecha;

    this.month_calendar = {
      year: nuevaFecha.getFullYear(),
      month: nuevaFecha.getMonth() + 1
    };

    this.limpiarSeleccionDia();

    this.messageMonth.emit(
      this.month_calendar
    );

    this.refresh.next();
  }

  /**
   * Limpia el día seleccionado al cambiar de mes.
   */
  private limpiarSeleccionDia(): void {

    if (this.selectedDay) {
      this.selectedDay.cssClass = '';
    }

    this.selectedDay = null;

    this.selectedDateInt = '';

    this.dia_inicial = '';

    this.emitioDiaInicial = false;
  }

  /**
   * Selección manual de un día.
   */
  click_day(dia: Date): void {

    this.dia_inicial =
      this.getDateInt(dia);

    this.selectedDateInt =
      this.dia_inicial;

    this.emitioDiaInicial = false;

    this.refresh.next();
  }

  /**
   * Se ejecuta antes de renderizar el mes.
   */
  beforeMonthViewRender(
    { body }: {
      body: CalendarMonthViewDay[]
    }
  ): void {

    body.forEach((day) => {

      const diaCInt =
        this.getDateInt(day.date);

      if (
        this.selectedDateInt &&
        diaCInt === this.selectedDateInt
      ) {

        day.cssClass =
          'amc-cal-day-selected';

        this.selectedDay = day;

        /**
         * Emite automáticamente el día inicial
         * una sola vez.
         */
        if (
          !this.emitioDiaInicial &&
          this.dia_inicial === diaCInt
        ) {

          this.emitioDiaInicial = true;

          setTimeout(() => {

            this.messageEvent.emit(day);

          }, 0);
        }

      } else {

        if (
          day.cssClass ===
          'amc-cal-day-selected'
        ) {

          day.cssClass = '';
        }
      }
    });
  }

}